
import React, { useState, useEffect } from 'react';
import { TrendingUpIcon } from './icons/TrendingUpIcon';
import { ClockIcon } from './icons/ClockIcon';
import { XCircleIcon } from './icons/XCircleIcon';
import { PlayCircleIcon } from './icons/PlayCircleIcon';
import { InformationCircleIcon } from './icons/InformationCircleIcon';
import { CheckCircleIcon } from './icons/CheckCircleIcon';

export const InterestSimulator: React.FC<{ userData?: any }> = ({ userData }) => {
    const [balance, setBalance] = useState(userData?.loanEstimatedBalance || 30000);
    const [rate, setRate] = useState(6.8);
    const [payment, setPayment] = useState(150);
    const [startYear, setStartYear] = useState(new Date().getFullYear() - 5);

    // Load Twitter Widget Script
    useEffect(() => {
        const scriptId = 'twitter-wjs';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = "https://platform.twitter.com/widgets.js";
            script.async = true;
            document.body.appendChild(script);
        } else {
            // If script exists, re-scan DOM for new widgets
            // @ts-ignore
            window.twttr?.widgets?.load();
        }
    }, []);

    const monthlyInterest = (balance * (rate / 100)) / 12;
    const monthlyGap = monthlyInterest - payment;
    const isTrapped = monthlyGap > 0;
    
    // Years Counted Calculation
    const currentYear = new Date().getFullYear();
    const yearsPaid = currentYear - startYear;
    const yearsRemaining = Math.max(0, 20 - yearsPaid); 
    const yearsOverpaid = Math.max(0, yearsPaid - 20);
    
    // Logic for "Red Zone" (Close to forgiveness but risky)
    const isRedZone = yearsPaid >= 15 && yearsPaid < 20;
    
    // Estimate money lost if overpaid
    const estimatedMoneyLost = yearsOverpaid * payment * 12;

    return (
        <div className="space-y-8 animate-fade-in">
             <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                <h3 className="text-xl font-bold text-red-900 mb-2">Stop The Interest Trap</h3>
                <p className="text-red-800">
                    You can pay for 10+ years and still see your balance go up—ForgivePath exists to stop that by getting you into the right federal plan (like SAVE) where unpaid interest is 100% subsidized.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 1. The Trap Visualizer */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <TrendingUpIcon className="w-5 h-5 text-blue-600" />
                        Interest Simulator
                    </h3>
                    
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Loan Balance</label>
                            <input type="number" value={balance} onChange={(e) => setBalance(Number(e.target.value))} className="w-full p-2 bg-gray-50 rounded border border-gray-300 text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Interest Rate %</label>
                            <input type="number" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-full p-2 bg-gray-50 rounded border border-gray-300 text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Your Payment</label>
                            <input type="number" value={payment} onChange={(e) => setPayment(Number(e.target.value))} className="w-full p-2 bg-gray-50 rounded border border-gray-300 text-sm" />
                        </div>
                    </div>

                    <div className="space-y-4">
                        {/* Scenario A: The Trap */}
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-center mb-2">
                                <span className="font-bold text-gray-700">Current Path (The Trap)</span>
                                <span className={`text-xs font-bold px-2 py-1 rounded ${isTrapped ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
                                    {isTrapped ? 'DANGER' : 'SAFE'}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">
                                Monthly Interest Charge: <strong>${monthlyInterest.toFixed(2)}</strong>
                            </p>
                            {isTrapped ? (
                                <p className="text-sm text-red-600 font-semibold">
                                    Result: Your balance <span className="underline">GROWS</span> by ${monthlyGap.toFixed(2)} every month, even while you pay.
                                </p>
                            ) : (
                                <p className="text-sm text-green-600 font-semibold">
                                    Result: Your payment covers interest. Balance decreases.
                                </p>
                            )}
                        </div>

                        {/* Scenario B: The Solution */}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg">RECOMMENDED</div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="font-bold text-blue-900">Federal SAVE Plan</span>
                            </div>
                            <p className="text-sm text-blue-800 mb-2">
                                We help you enroll in the <strong>Federal SAVE Plan</strong>. Under this plan's rules, any interest not covered by your payment is 100% subsidized by the government.
                            </p>
                            <p className="text-sm text-green-700 font-bold bg-green-50 p-2 rounded border border-green-200 inline-block">
                                Result: Balance Growth is $0.00. Guaranteed.
                            </p>
                        </div>
                    </div>
                </div>

                {/* 2. Years Counted Audit */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <ClockIcon className="w-5 h-5 text-blue-600" />
                            "Years Counted" Audit
                        </h3>
                    </div>

                    {/* Step 1: Selector - MOVED TO TOP */}
                    <div className="mb-6">
                        <label className="block text-xs font-semibold text-gray-500 mb-1">When did you enter repayment?</label>
                        <select 
                            value={startYear} 
                            onChange={(e) => setStartYear(Number(e.target.value))}
                            className="w-full p-3 bg-gray-50 rounded-lg border border-gray-300 text-sm"
                        >
                            {Array.from({length: 30}, (_, i) => currentYear - i).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    {/* Step 2: Visual Indicator */}
                    <div className="flex-grow flex flex-col justify-center items-center bg-gray-50 rounded-xl p-6 border border-gray-100 relative overflow-hidden mb-6">
                        <div className="text-center z-10">
                            <p className="text-gray-500 text-sm uppercase tracking-wide">Estimated Progress</p>
                            <div className={`text-5xl font-extrabold my-2 ${yearsOverpaid > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                                {yearsPaid} / 20
                            </div>
                            <p className="text-gray-500 text-sm font-medium">Years Completed</p>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4 mt-6 overflow-hidden z-10">
                            <div 
                                className={`h-full transition-all duration-1000 ${yearsOverpaid > 0 ? 'bg-red-500' : 'bg-green-500'}`} 
                                style={{ width: `${Math.min(100, (yearsPaid/20)*100)}%` }}
                            ></div>
                        </div>

                        {yearsOverpaid > 0 ? (
                            <div className="mt-6 bg-red-100 border border-red-200 rounded-xl p-4 text-center w-full animate-pulse">
                                <div className="flex items-center justify-center gap-2 text-red-800 font-bold uppercase text-xs tracking-wider mb-1">
                                    <XCircleIcon className="w-4 h-4" /> Critical Alert
                                </div>
                                <p className="text-red-900 font-bold text-lg leading-tight">
                                    You have overpaid for {yearsOverpaid} years.
                                </p>
                                <p className="text-red-700 text-sm mt-1">
                                    That's approx. <span className="font-extrabold text-xl bg-white px-1 rounded text-red-600 border border-red-200 mx-1">${estimatedMoneyLost.toLocaleString()}</span> left on the table.
                                </p>
                                <p className="text-[10px] text-red-600 mt-2">
                                    Why not forgiven yet? You may have "FFEL" loans. You MUST consolidate them to Direct Loans to get this forgiveness.
                                </p>
                            </div>
                        ) : isRedZone ? (
                            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-left w-full">
                                <div className="flex items-center gap-2 text-yellow-800 font-bold uppercase text-xs tracking-wider mb-1">
                                    <InformationCircleIcon className="w-4 h-4" /> Close to the Finish Line
                                </div>
                                <p className="text-yellow-900 font-bold text-sm leading-tight">
                                    You have {yearsRemaining} years left. Careful!
                                </p>
                                <p className="text-yellow-800 text-xs mt-2">
                                    <strong>Do not blindly consolidate</strong> or switch plans without checking your loan type. 
                                </p>
                                <ul className="list-disc list-inside text-[10px] text-yellow-800 mt-2 space-y-1">
                                    <li>If you have <strong>Direct Loans</strong>, the adjustment is automatic.</li>
                                    <li>If you have <strong>FFEL Loans</strong>, you MUST consolidate immediately to keep your {yearsPaid} years of credit.</li>
                                </ul>
                            </div>
                        ) : (
                            <div className="mt-4 text-center z-10">
                                <p className="text-sm text-gray-600">
                                    You likely only have <strong>{yearsRemaining} years</strong> left until 100% forgiveness.*
                                </p>
                                <p className="text-[10px] text-gray-400 mt-2">*Estimate for undergraduate loans on SAVE plan.</p>
                            </div>
                        )}
                    </div>

                    {/* Step 3: Video Case Study & Explanation - REDESIGNED */}
                    <div className="border-t border-gray-100 pt-8 mt-auto">
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide mb-2">
                                <PlayCircleIcon className="w-4 h-4" /> Watch Case Study
                            </div>
                            <h4 className="text-lg font-bold text-gray-900">The 18-Year Trap: Why Borrowers Are Scared to Switch</h4>
                        </div>
                        
                        <div className="max-w-md mx-auto mb-8 rounded-xl overflow-hidden border border-gray-200 shadow-md">
                             <blockquote className="twitter-tweet" data-dnt="true" data-theme="light">
                                <p lang="en" dir="ltr">The 18-Year Trap: A Case Study</p>
                                <a href="https://twitter.com/i/status/2000191352055939247">Loading Case Study...</a>
                            </blockquote>
                        </div>

                        <div className="bg-red-50 rounded-xl p-6 border border-red-100 text-left">
                            <h5 className="font-bold text-red-900 text-base mb-3 flex items-center gap-2">
                                <XCircleIcon className="w-5 h-5" />
                                The Dangerous Myth: "Switching Plans Restarts My Clock"
                            </h5>
                            <div className="text-sm text-gray-800 leading-relaxed mb-6">
                                <p className="mb-2">
                                    In this video, the borrower is terrified that if she switches to the new <strong>SAVE Plan</strong> to lower her payments, her <strong>forgiveness clock will reset to zero</strong>—erasing 18 years of progress.
                                </p>
                                <p>
                                    <strong>This used to be true, but rules have changed.</strong> Under the <span className="font-bold text-blue-700">IDR Account Adjustment</span>, the Dept of Ed now allows you to switch (and consolidate) <strong>WITHOUT losing your past credit</strong>.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Strategy Column */}
                                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                    <p className="font-bold text-gray-900 mb-2 border-b pb-1">The Strategy</p>
                                    <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                                        <li><strong>Don't Panic:</strong> You keep your {yearsPaid} years of credit via the Adjustment.</li>
                                        <li><strong>Switch to SAVE:</strong> This stops your balance from growing (100% interest subsidy).</li>
                                    </ul>
                                </div>

                                {/* ForgivePath Solution Column */}
                                <div className="bg-green-50 p-4 rounded-lg border border-green-200 shadow-sm relative overflow-hidden">
                                    <div className="absolute top-0 right-0 bg-green-200 text-green-800 text-[10px] font-bold px-2 py-1 rounded-bl-lg">INCLUDED</div>
                                    <p className="font-bold text-green-900 mb-2 border-b border-green-200 pb-1 flex items-center gap-2">
                                        <CheckCircleIcon className="w-4 h-4" />
                                        How We Handle It For You
                                    </p>
                                    <p className="text-xs text-green-800 mb-2">
                                        We automate this entire process so you don't make a mistake.
                                    </p>
                                    <ul className="list-disc list-inside space-y-1 text-sm text-green-800 font-medium">
                                        <li>We detect if you need to consolidate (safe to do now).</li>
                                        <li>We generate the application to enroll you in SAVE immediately.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
